import {
  Box,
  Flex,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuProps,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { RiAddFill, RiCloseFill } from "@remixicon/react";
import { AnimatePresence } from "framer-motion";
import partition from "lodash/partition";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import { useFeatureViewsData } from "@/app/data/useFeatureViewsData";
import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { IcoFeatureView } from "@/common/components/Icons";
import MenuDescription from "@/common/components/MenuDescription";
import MotionListItem from "@/common/components/MotionListItem";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { FeatureViewDTO } from "@reflag/shared/featureViewAPI";
import { FeatureViewUrl } from "@reflag/shared/urls";

import { useFeatureFeatureViews } from "../data/useFeatureFeatureViews";
import {
  useAddToFeatureViewMutation,
  useAddToNewFeatureViewMutation,
  useRemoveFromFeatureViewMutation,
} from "../data/useFeatureViewMutations";

type FeatureViewsSelectorProps = {
  featureId?: string;
  popupMenuPlacement?: MenuProps["placement"];
  maxFeatureViewsToShow?: number;
};

function FeatureViewsSelector({
  featureId,
  popupMenuPlacement = "bottom-start",
  maxFeatureViewsToShow = 10,
}: FeatureViewsSelectorProps) {
  const { env } = useEnvironment();

  const { data: allFeatureViews, isLoading: isListLoading } =
    useFeatureViewsData();
  const { data: selectedFeatureViewsIds = [], isLoading: isCurrentLoading } =
    useFeatureFeatureViews(featureId);

  const [searchTerm, setSearchTerm] = useState("");
  const [focusMode, setFocusMode] = useState<-1 | 0 | 1>(0);

  const [selectedFeatureViews, availableFeatureViews] = useMemo(() => {
    const custom = (allFeatureViews || []).filter(
      (view) => view.type === "custom",
    );

    return partition(custom, (view) =>
      selectedFeatureViewsIds.includes(view.id),
    );
  }, [allFeatureViews, selectedFeatureViewsIds]);

  const [filteredFeatureViews, showCreateNew] = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const fil = availableFeatureViews.filter((view) =>
      view.name.toLowerCase().includes(term),
    );

    const cre =
      searchTerm.length > 0 &&
      fil.findIndex((view) => view.name.toLowerCase() === term) === -1;

    return [fil, cre];
  }, [searchTerm, availableFeatureViews]);

  const { mutate: addFeatureView } = useAddToFeatureViewMutation(featureId);
  const { mutate: removeFeatureView } =
    useRemoveFromFeatureViewMutation(featureId);

  const { mutate: addNewFeatureView } =
    useAddToNewFeatureViewMutation(featureId);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);
  const lastMenuItemRef = useRef<HTMLButtonElement>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleMenuClose = () => {
    setSearchTerm("");
    onClose();
  };

  const handleFeatureViewAdd = (view: FeatureViewDTO) => {
    addFeatureView(view);
    handleMenuClose();
  };

  const handleFeatureViewRemove = (view: FeatureViewDTO) => {
    removeFeatureView(view);
    handleMenuClose();
  };

  const handleCreateFeatureView = (name: string) => {
    name = name.trim();
    if (name) {
      addNewFeatureView(name);
      handleMenuClose();
    }
  };

  useEffect(() => {
    switch (focusMode) {
      case -1:
        (lastMenuItemRef.current || firstMenuItemRef.current)?.focus();
        break;
      case 0:
        searchInputRef.current?.focus();
        break;
      case 1:
        (firstMenuItemRef.current || lastMenuItemRef.current)?.focus();
        break;
    }
  }, [focusMode]);

  const isLoading = isListLoading || isCurrentLoading;
  return (
    <Flex direction="column">
      <Flex align="center" direction="row">
        <SectionHeading variant="subtle">
          {selectedFeatureViews.length === 0 ? "Add to views" : "Views"}
        </SectionHeading>
        <Spacer />
        {isLoading && !isOpen ? (
          <Box mx={2}>
            <Spinner size="xs" variant="subtle" />
          </Box>
        ) : (
          <Menu
            autoSelect={true}
            closeOnSelect={false}
            initialFocusRef={searchInputRef}
            isLazy={true}
            isOpen={isOpen}
            placement={popupMenuPlacement}
            onClose={handleMenuClose}
          >
            <Restricted role="contributor">
              <MenuButton
                aria-label="Add to view"
                as={IconButton}
                color="dimmed"
                icon={<RiAddFill size={16} />}
                size="xs"
                variant="ghost"
                onClick={onOpen}
              />
            </Restricted>
            <MenuList
              onFocus={() => focusMode === 0 && searchInputRef.current?.focus()}
              onKeyDown={(e) => {
                if (e.key === "Tab" && focusMode !== 0) {
                  searchInputRef.current?.focus();
                }
              }}
            >
              <MenuDescription>Select view to add to:</MenuDescription>
              <Box p={1}>
                <Input
                  ref={searchInputRef}
                  placeholder="View name..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  onFocus={() => setFocusMode(0)}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "Tab") {
                      setFocusMode(-1);
                    } else if (e.key === "ArrowDown") {
                      setFocusMode(1);
                    }
                  }}
                />
              </Box>
              <MenuDivider my={0} />
              {filteredFeatureViews
                .slice(0, maxFeatureViewsToShow)
                .map((view, index) => (
                  <MenuItem
                    key={view.id}
                    ref={
                      index === 0
                        ? firstMenuItemRef
                        : index === filteredFeatureViews.length - 1
                          ? lastMenuItemRef
                          : undefined
                    }
                    closeOnSelect={true}
                    icon={<IcoFeatureView size="md" />}
                    iconSpacing={1}
                    onClick={() => handleFeatureViewAdd(view)}
                  >
                    {highlightStringMatch(view.name, searchTerm)}
                  </MenuItem>
                ))}
              <MenuDivider my={0} />
              {showCreateNew && (
                <MenuItem
                  key="create-new"
                  ref={lastMenuItemRef}
                  icon={<RiAddFill size={16} />}
                  iconSpacing={1}
                  isFocusable={true}
                  onClick={() => handleCreateFeatureView(searchTerm)}
                >
                  Create <strong>{searchTerm}</strong>
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        )}
      </Flex>
      <AnimatePresence initial={false}>
        {selectedFeatureViews.map((view) => (
          <MotionListItem key={view.id}>
            <Flex align="center" justify="space-between" width="100%">
              <Link
                as={RouterLink}
                textDecoration={"none"}
                to={FeatureViewUrl(env, view.id)}
              >
                <HStack w={"100%"}>
                  <Box color="dimmed">
                    <IcoFeatureView size="md" />
                  </Box>
                  <Text fontSize="sm">{view.name}</Text>
                </HStack>
              </Link>
              <Restricted role="contributor">
                <IconButton
                  aria-label="Remove from view"
                  color="dimmed"
                  size="xs"
                  variant="ghost"
                  onClick={() => handleFeatureViewRemove(view)}
                >
                  <RiCloseFill size={16} />
                </IconButton>
              </Restricted>
            </Flex>
          </MotionListItem>
        ))}
      </AnimatePresence>
    </Flex>
  );
}

export default FeatureViewsSelector;
